package com.havn.app.features.analytics.presentation

import androidx.compose.foundation.Canvas
import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Size
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.StrokeCap
import androidx.compose.ui.graphics.drawscope.Stroke
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import com.havn.app.core.designsystem.HavnButton
import com.havn.app.core.designsystem.HavnButtonStyle
import com.havn.app.core.designsystem.HavnCard
import com.havn.app.core.designsystem.HavnIcon
import com.havn.app.core.designsystem.HavnIconKey
import com.havn.app.core.designsystem.HavnScreenFrame
import com.havn.app.core.designsystem.HavnStatCard
import com.havn.app.core.designsystem.havnIsWebShell
import com.havn.app.features.agency.domain.AgencyMember
import com.havn.app.features.agency.presentation.AgencyViewModel
import com.havn.app.features.followups.domain.FollowUp
import com.havn.app.features.followups.domain.FollowUpChannel
import com.havn.app.features.followups.domain.FollowUpStatus
import com.havn.app.features.followups.presentation.FollowUpsViewModel
import com.havn.app.features.leads.domain.Lead
import com.havn.app.features.leads.presentation.LeadsViewModel
import com.havn.app.features.openhouses.domain.LeadTemperature
import com.havn.app.features.openhouses.domain.OpenHouse
import com.havn.app.features.openhouses.presentation.OpenHousesViewModel
import com.havn.app.features.properties.domain.Property
import com.havn.app.features.properties.presentation.PropertiesViewModel
import com.havn.app.ui.theme.LocalHavnThemeTokens
import kotlin.math.roundToInt

@Composable
fun AnalyticsScreen(
    propertiesViewModel: PropertiesViewModel,
    openHousesViewModel: OpenHousesViewModel,
    leadsViewModel: LeadsViewModel,
    followUpsViewModel: FollowUpsViewModel,
    agencyViewModel: AgencyViewModel,
    onBackToToday: (() -> Unit)? = null
) {
    val propertiesState by propertiesViewModel.uiState.collectAsState()
    val openHousesState by openHousesViewModel.uiState.collectAsState()
    val leadsState by leadsViewModel.uiState.collectAsState()
    val followUpsState by followUpsViewModel.uiState.collectAsState()
    val agencyState by agencyViewModel.uiState.collectAsState()
    val theme = LocalHavnThemeTokens.current
    var dateFilter by remember { mutableStateOf(AnalyticsDateFilter.All) }
    var agentFilterId by remember { mutableStateOf<String?>(null) }

    val agentOptions = remember(agencyState.members, propertiesState.currentAgentId) {
        buildAgentOptions(agencyState.members, propertiesState.currentAgentId)
    }
    val filteredData = remember(
        dateFilter,
        agentFilterId,
        propertiesState.properties,
        openHousesState.openHouses,
        leadsState.leads,
        followUpsState.followUps
    ) {
        filterAnalyticsData(
            dateFilter = dateFilter,
            agentId = agentFilterId,
            properties = propertiesState.properties,
            openHouses = openHousesState.openHouses,
            leads = leadsState.leads,
            followUps = followUpsState.followUps
        )
    }

    val metrics = buildAnalyticsMetrics(
        properties = filteredData.properties,
        openHouses = filteredData.openHouses,
        leads = filteredData.leads,
        followUps = filteredData.followUps,
        members = agencyState.members
    )

    HavnScreenFrame(
        eyebrow = "",
        title = "Analytics Overview",
        subtitle = "",
        backLabel = onBackToToday?.let { "Today" },
        onBack = onBackToToday,
        isRefreshing = propertiesState.isLoading || openHousesState.isLoading || leadsState.isLoading || followUpsState.isLoading,
        onRefresh = {
            propertiesViewModel.refresh()
            openHousesViewModel.refresh()
            leadsViewModel.refresh()
            followUpsViewModel.refresh()
            agencyViewModel.refresh()
        }
    ) {
        BoxWithConstraints(
            Modifier
                .fillMaxSize()
                .background(Brush.verticalGradient(listOf(theme.background, theme.gradientStart, theme.background)))
        ) {
            val wide = havnIsWebShell && maxWidth >= 980.dp
            Column(
                modifier = Modifier.fillMaxWidth(),
                verticalArrangement = Arrangement.spacedBy(14.dp)
            ) {
                AnalyticsToolbar(
                    dateRange = dateFilter.labelFor(filteredData.activityDateRange ?: metrics.dateRange),
                    agentLabel = agentOptions.firstOrNull { it.agentId == agentFilterId }?.label ?: "All agents",
                    onDateClick = { dateFilter = dateFilter.next() },
                    onAgentClick = { agentFilterId = agentOptions.nextAgentId(agentFilterId) }
                )

                AnalyticsKpiGrid(
                    metrics = metrics,
                    wide = wide
                )

                if (wide) {
                    Row(horizontalArrangement = Arrangement.spacedBy(14.dp), verticalAlignment = Alignment.Top) {
                        FunnelCard(metrics, Modifier.weight(0.9f))
                        Column(modifier = Modifier.weight(1.35f), verticalArrangement = Arrangement.spacedBy(14.dp)) {
                            TrendCard(metrics.trendSeries, Modifier.fillMaxWidth())
                            InsightCard(metrics.insightText, Modifier.fillMaxWidth())
                        }
                    }
                    Row(horizontalArrangement = Arrangement.spacedBy(14.dp), verticalAlignment = Alignment.Top) {
                        TopPropertiesCard(metrics.topProperties, Modifier.weight(1.15f))
                        Column(modifier = Modifier.weight(1f), verticalArrangement = Arrangement.spacedBy(14.dp)) {
                            LeadQualityCard(metrics.hotLeads, metrics.warmLeads, metrics.coldLeads, Modifier.fillMaxWidth())
                            FollowUpCard(metrics.followUpRate, metrics.completedFollowUps, metrics.totalLeads, Modifier.fillMaxWidth())
                        }
                    }
                    Row(horizontalArrangement = Arrangement.spacedBy(14.dp), verticalAlignment = Alignment.Top) {
                        AgentPerformanceCard(metrics.agentRows, Modifier.weight(1.15f))
                        ChannelBreakdownCard(metrics.channelRows, Modifier.weight(1f))
                    }
                } else {
                    FunnelCard(metrics, Modifier.fillMaxWidth())
                    TrendCard(metrics.trendSeries, Modifier.fillMaxWidth())
                    InsightCard(metrics.insightText, Modifier.fillMaxWidth())
                    TopPropertiesCard(metrics.topProperties, Modifier.fillMaxWidth())
                    LeadQualityCard(metrics.hotLeads, metrics.warmLeads, metrics.coldLeads, Modifier.fillMaxWidth())
                    FollowUpCard(metrics.followUpRate, metrics.completedFollowUps, metrics.totalLeads, Modifier.fillMaxWidth())
                    AgentPerformanceCard(metrics.agentRows, Modifier.fillMaxWidth())
                    ChannelBreakdownCard(metrics.channelRows, Modifier.fillMaxWidth())
                }
            }
        }
    }
}

@Composable
private fun AnalyticsToolbar(
    dateRange: String,
    agentLabel: String,
    onDateClick: () -> Unit,
    onAgentClick: () -> Unit
) {
    Row(
        modifier = Modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.spacedBy(10.dp, Alignment.End),
        verticalAlignment = Alignment.CenterVertically
    ) {
        FilterPill(dateRange, HavnIconKey.Calendar, onClick = onDateClick)
        FilterPill(agentLabel, HavnIconKey.User, onClick = onAgentClick)
        FilterPill("Filters", HavnIconKey.Sliders)
    }
}

@Composable
private fun AnalyticsKpiGrid(metrics: AnalyticsMetrics, wide: Boolean) {
    if (wide) {
        Row(horizontalArrangement = Arrangement.spacedBy(12.dp), modifier = Modifier.fillMaxWidth()) {
            KpiCard("Total Visitors", metrics.visitors.toString(), "${metrics.openHouseCount} open houses", HavnIconKey.Users, Color(0xFF22D3EE), Modifier.weight(1f))
            KpiCard("Hot Leads", metrics.hotLeads.toString(), "${metrics.warmLeads} warm leads", HavnIconKey.TrendUp, Color(0xFFF97316), Modifier.weight(1f))
            KpiCard("Tour Signals", metrics.tourSignals.toString(), "from notes/follow-ups", HavnIconKey.Calendar, Color(0xFF22C55E), Modifier.weight(1f))
            KpiCard("Hot Conversion", "${metrics.conversion.oneDecimal()}%", "hot leads / visitors", HavnIconKey.Chart, Color(0xFFE11D48), Modifier.weight(1f))
            KpiCard("Avg. Intent", metrics.averageIntentLabel, "visitor intent score", HavnIconKey.Today, Color(0xFF8B5CF6), Modifier.weight(1f))
        }
    } else {
        Column(verticalArrangement = Arrangement.spacedBy(10.dp)) {
            Row(horizontalArrangement = Arrangement.spacedBy(10.dp), modifier = Modifier.fillMaxWidth()) {
                KpiCard("Visitors", metrics.visitors.toString(), "${metrics.openHouseCount} open houses", HavnIconKey.Users, Color(0xFF22D3EE), Modifier.weight(1f))
                KpiCard("Hot Leads", metrics.hotLeads.toString(), "${metrics.warmLeads} warm", HavnIconKey.TrendUp, Color(0xFFF97316), Modifier.weight(1f))
            }
            Row(horizontalArrangement = Arrangement.spacedBy(10.dp), modifier = Modifier.fillMaxWidth()) {
                KpiCard("Tour Signals", metrics.tourSignals.toString(), "from text", HavnIconKey.Calendar, Color(0xFF22C55E), Modifier.weight(1f))
                KpiCard("Conversion", "${metrics.conversion.oneDecimal()}%", "hot / visitors", HavnIconKey.Chart, Color(0xFFE11D48), Modifier.weight(1f))
            }
        }
    }
}

@Composable
private fun KpiCard(title: String, value: String, delta: String, icon: HavnIconKey, accent: Color, modifier: Modifier) {
    val theme = LocalHavnThemeTokens.current
    HavnCard(modifier = modifier, border = BorderStroke(1.dp, theme.border)) {
        Column(verticalArrangement = Arrangement.spacedBy(10.dp)) {
            Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                HavnIcon(icon, contentDescription = title, tint = accent, modifier = Modifier.size(20.dp))
                Text(title, color = theme.primaryText, style = MaterialTheme.typography.labelLarge, maxLines = 1, overflow = TextOverflow.Ellipsis)
            }
            Text(value, color = theme.primaryText, style = MaterialTheme.typography.headlineMedium)
            Text(delta, color = Color(0xFF4ADE80), style = MaterialTheme.typography.labelLarge)
        }
    }
}

@Composable
private fun FilterPill(label: String, icon: HavnIconKey, onClick: (() -> Unit)? = null) {
    val theme = LocalHavnThemeTokens.current
    Surface(
        color = theme.card,
        shape = MaterialTheme.shapes.medium,
        border = BorderStroke(1.dp, theme.border),
        modifier = if (onClick == null) Modifier else Modifier.clickable(onClick = onClick)
    ) {
        Row(
            modifier = Modifier.padding(horizontal = 14.dp, vertical = 11.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(8.dp)
        ) {
            HavnIcon(icon, contentDescription = label, tint = theme.secondaryText, modifier = Modifier.size(17.dp))
            Text(label, color = theme.primaryText, style = MaterialTheme.typography.labelLarge, maxLines = 1)
        }
    }
}

@Composable
private fun FunnelCard(metrics: AnalyticsMetrics, modifier: Modifier) {
    val theme = LocalHavnThemeTokens.current
    HavnCard(modifier = modifier) {
        Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
            Text("Property Funnel", color = theme.primaryText, style = MaterialTheme.typography.titleMedium)
            Text("From property activity to qualified buyer momentum", color = theme.secondaryText, style = MaterialTheme.typography.bodyMedium)
            val stages = listOf(
                FunnelStage(metrics.activeProperties.coerceAtLeast(0), "Properties", Color(0xFF2563EB)),
                FunnelStage(metrics.visitors.coerceAtLeast(0), "Visitors", Color(0xFF0EA5E9)),
                FunnelStage(metrics.interestedBuyers.coerceAtLeast(0), "Interested Buyers", Color(0xFF10B981)),
                FunnelStage(metrics.hotLeads.coerceAtLeast(0), "Hot Leads", Color(0xFFF59E0B)),
                FunnelStage(metrics.tourSignals.coerceAtLeast(0), "Tour Signals", Color(0xFF8B5CF6)),
                FunnelStage(metrics.offerSignals.coerceAtLeast(0), "Offer Signals", Color(0xFFDB2777))
            )
            stages.forEachIndexed { index, stage ->
                FunnelBar(stage, widthFraction = (1f - index * 0.15f).coerceAtLeast(0.38f))
            }
            HavnButton(
                text = "View full funnel report",
                onClick = {},
                style = HavnButtonStyle.Ghost,
                leadingIcon = HavnIconKey.ArrowRight,
                modifier = Modifier.fillMaxWidth()
            )
        }
    }
}

@Composable
private fun FunnelBar(stage: FunnelStage, widthFraction: Float) {
    Box(modifier = Modifier.fillMaxWidth(), contentAlignment = Alignment.Center) {
        Surface(
            modifier = Modifier.fillMaxWidth(widthFraction).height(46.dp),
            color = stage.color.copy(alpha = 0.76f),
            shape = MaterialTheme.shapes.small
        ) {
            Column(horizontalAlignment = Alignment.CenterHorizontally, verticalArrangement = Arrangement.Center) {
                Text(stage.value.toString(), color = Color.White, style = MaterialTheme.typography.titleMedium)
                Text(stage.label, color = Color.White, style = MaterialTheme.typography.labelSmall)
            }
        }
    }
}

@Composable
private fun TrendCard(series: List<ChartSeries>, modifier: Modifier) {
    val theme = LocalHavnThemeTokens.current
    HavnCard(modifier = modifier) {
        Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
            Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween, verticalAlignment = Alignment.CenterVertically) {
                Text("Performance Over Time", color = theme.primaryText, style = MaterialTheme.typography.titleMedium)
                FilterPill("30 Days", HavnIconKey.Calendar)
            }
            LineChart(
                series = series,
                modifier = Modifier.fillMaxWidth().height(250.dp)
            )
        }
    }
}

@Composable
private fun LineChart(series: List<ChartSeries>, modifier: Modifier) {
    val theme = LocalHavnThemeTokens.current
    Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
        Row(horizontalArrangement = Arrangement.spacedBy(14.dp)) {
            series.forEach {
                Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(6.dp)) {
                    Surface(Modifier.size(8.dp), shape = CircleShape, color = it.color) {}
                    Text(it.label, color = theme.secondaryText, style = MaterialTheme.typography.labelSmall)
                }
            }
        }
        Canvas(modifier) {
            val allValues = series.flatMap { it.values }
            val maxValue = allValues.maxOrNull()?.coerceAtLeast(1) ?: 1
            repeat(5) { index ->
                val y = size.height * index / 4f
                drawLine(theme.border.copy(alpha = 0.45f), Offset(0f, y), Offset(size.width, y), strokeWidth = 1f)
            }
            series.forEach { chartSeries ->
                val step = size.width / (chartSeries.values.size - 1).coerceAtLeast(1)
                val points = chartSeries.values.mapIndexed { index, value ->
                    Offset(index * step, size.height - value.toFloat() / maxValue.toFloat() * size.height)
                }
                points.zipWithNext().forEach { (a, b) ->
                    drawLine(chartSeries.color, a, b, strokeWidth = 3f, cap = StrokeCap.Round)
                }
                points.forEach { drawCircle(chartSeries.color, radius = 3.5f, center = it) }
            }
        }
    }
}

@Composable
private fun InsightCard(insight: String, modifier: Modifier) {
    val theme = LocalHavnThemeTokens.current
    HavnCard(modifier = modifier, border = BorderStroke(1.dp, theme.primaryAccent.copy(alpha = 0.35f))) {
        Row(horizontalArrangement = Arrangement.spacedBy(14.dp), verticalAlignment = Alignment.CenterVertically) {
            Column(modifier = Modifier.weight(1f), verticalArrangement = Arrangement.spacedBy(10.dp)) {
                Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                    HavnIcon(HavnIconKey.Star, contentDescription = "AI Insight", tint = theme.primaryAccent, modifier = Modifier.size(22.dp))
                    Text("AI Insight", color = theme.primaryText, style = MaterialTheme.typography.titleMedium)
                }
                Text(
                    insight,
                    color = theme.secondaryText,
                    style = MaterialTheme.typography.bodyMedium
                )
                HavnButton("View AI insights", onClick = {}, style = HavnButtonStyle.Secondary, leadingIcon = HavnIconKey.ArrowRight)
            }
            Surface(
                modifier = Modifier.size(124.dp),
                color = theme.primaryAccent.copy(alpha = 0.16f),
                shape = MaterialTheme.shapes.medium
            ) {
                Box(
                    modifier = Modifier.background(
                        Brush.linearGradient(listOf(Color(0xFF4C1D95), Color(0xFF0F766E), Color(0xFFF59E0B)))
                    )
                )
            }
        }
    }
}

@Composable
private fun TopPropertiesCard(properties: List<Property>, modifier: Modifier) {
    val theme = LocalHavnThemeTokens.current
    HavnCard(modifier = modifier) {
        Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
            Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween) {
                Text("Top Performing Properties", color = theme.primaryText, style = MaterialTheme.typography.titleMedium)
                HavnButton("View all", onClick = {}, style = HavnButtonStyle.Secondary)
            }
            properties.ifEmpty { emptyList() }.forEachIndexed { index, property ->
                PropertyPerformanceRow(index + 1, property)
            }
            if (properties.isEmpty()) {
                Text("No property analytics yet.", color = theme.secondaryText, style = MaterialTheme.typography.bodyMedium)
            }
        }
    }
}

@Composable
private fun PropertyPerformanceRow(rank: Int, property: Property) {
    val theme = LocalHavnThemeTokens.current
    Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(10.dp), verticalAlignment = Alignment.CenterVertically) {
        Surface(Modifier.size(24.dp), shape = CircleShape, color = theme.primaryAccent.copy(alpha = 0.9f)) {
            Box(contentAlignment = Alignment.Center) {
                Text(rank.toString(), color = theme.background, style = MaterialTheme.typography.labelSmall)
            }
        }
        Column(modifier = Modifier.weight(1f)) {
            Text(property.title, color = theme.primaryText, style = MaterialTheme.typography.labelLarge, maxLines = 1, overflow = TextOverflow.Ellipsis)
            Text(property.displayCity, color = theme.secondaryText, style = MaterialTheme.typography.labelSmall, maxLines = 1, overflow = TextOverflow.Ellipsis)
        }
        Text(property.stats.totalVisitors.toString(), color = theme.primaryText, style = MaterialTheme.typography.bodyLarge)
        Text(property.stats.hotLeads.toString(), color = theme.primaryText, style = MaterialTheme.typography.bodyLarge)
        Text("${property.stats.conversionRate.roundToInt()}%", color = Color(0xFF4ADE80), style = MaterialTheme.typography.labelLarge)
    }
}

@Composable
private fun LeadQualityCard(hot: Int, warm: Int, cold: Int, modifier: Modifier) {
    val theme = LocalHavnThemeTokens.current
    val total = (hot + warm + cold).coerceAtLeast(1)
    HavnCard(modifier = modifier) {
        Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
            Text("Lead Quality Breakdown", color = theme.primaryText, style = MaterialTheme.typography.titleMedium)
            Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(18.dp)) {
                DonutChart(listOf(hot to Color(0xFFDB2777), warm to Color(0xFFF59E0B), cold to Color(0xFF3B82F6)), Modifier.size(150.dp))
                Column(verticalArrangement = Arrangement.spacedBy(10.dp)) {
                    LegendRow("Hot Leads", hot, hot * 100 / total, Color(0xFFDB2777))
                    LegendRow("Warm Leads", warm, warm * 100 / total, Color(0xFFF59E0B))
                    LegendRow("Cold Leads", cold, cold * 100 / total, Color(0xFF3B82F6))
                }
            }
        }
    }
}

@Composable
private fun DonutChart(values: List<Pair<Int, Color>>, modifier: Modifier) {
    val theme = LocalHavnThemeTokens.current
    Canvas(modifier) {
        val total = values.sumOf { it.first }.coerceAtLeast(1)
        var start = -90f
        values.forEach { (value, color) ->
            val sweep = value.toFloat() / total.toFloat() * 360f
            drawArc(color, startAngle = start, sweepAngle = sweep, useCenter = false, topLeft = Offset(12f, 12f), size = Size(size.width - 24f, size.height - 24f), style = Stroke(width = 28f, cap = StrokeCap.Butt))
            start += sweep
        }
        drawCircle(theme.background, radius = size.minDimension * 0.25f, center = center)
    }
}

@Composable
private fun LegendRow(label: String, value: Int, percent: Int, color: Color) {
    val theme = LocalHavnThemeTokens.current
    Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(8.dp)) {
        Surface(Modifier.size(9.dp), shape = CircleShape, color = color) {}
        Text("$label $value ($percent%)", color = theme.primaryText, style = MaterialTheme.typography.labelLarge)
    }
}

@Composable
private fun FollowUpCard(rate: Int, completed: Int, total: Int, modifier: Modifier) {
    val theme = LocalHavnThemeTokens.current
    HavnCard(modifier = modifier) {
        Row(horizontalArrangement = Arrangement.spacedBy(18.dp), verticalAlignment = Alignment.CenterVertically) {
            DonutChart(listOf(rate to theme.primaryAccent, (100 - rate) to theme.border), Modifier.size(112.dp))
            Column(verticalArrangement = Arrangement.spacedBy(6.dp)) {
                Text("Follow-Up Performance", color = theme.primaryText, style = MaterialTheme.typography.titleMedium)
                Text("$completed of $total leads followed up", color = theme.primaryText, style = MaterialTheme.typography.bodyLarge)
                Text("${(total - completed).coerceAtLeast(0)} leads still need follow-up", color = Color(0xFF4ADE80), style = MaterialTheme.typography.labelLarge)
            }
        }
    }
}

@Composable
private fun AgentPerformanceCard(rows: List<AgentPerformanceRow>, modifier: Modifier) {
    val theme = LocalHavnThemeTokens.current
    HavnCard(modifier = modifier) {
        Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
            Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween) {
                Text("Agent Performance", color = theme.primaryText, style = MaterialTheme.typography.titleMedium)
                HavnButton("View team report", onClick = {}, style = HavnButtonStyle.Secondary)
            }
            rows.take(5).forEach { row ->
                AgentRow(row.name, row.openHouses, row.hotLeads, row.followRate, row.conversion)
            }
            if (rows.isEmpty()) {
                Text("No agent activity yet.", color = theme.secondaryText, style = MaterialTheme.typography.bodyMedium)
            }
        }
    }
}

@Composable
private fun AgentRow(name: String, openings: Int, hotLeads: Int, followRate: Int, conversion: Double) {
    val theme = LocalHavnThemeTokens.current
    Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(10.dp), verticalAlignment = Alignment.CenterVertically) {
        Surface(Modifier.size(30.dp), shape = CircleShape, color = theme.primaryAccent.copy(alpha = 0.22f)) {
            Box(contentAlignment = Alignment.Center) { HavnIcon(HavnIconKey.User, contentDescription = name, tint = theme.primaryAccent, modifier = Modifier.size(16.dp)) }
        }
        Text(name, color = theme.primaryText, style = MaterialTheme.typography.labelLarge, modifier = Modifier.weight(1f), maxLines = 1, overflow = TextOverflow.Ellipsis)
        Text(openings.toString(), color = theme.secondaryText, style = MaterialTheme.typography.labelLarge)
        Text(hotLeads.toString(), color = theme.secondaryText, style = MaterialTheme.typography.labelLarge)
        Text("$followRate%", color = Color(0xFF4ADE80), style = MaterialTheme.typography.labelLarge)
        Text("${conversion.oneDecimal()}%", color = theme.primaryText, style = MaterialTheme.typography.labelLarge)
    }
}

@Composable
private fun ChannelBreakdownCard(rows: List<SourceRow>, modifier: Modifier) {
    val theme = LocalHavnThemeTokens.current
    HavnCard(modifier = modifier) {
        Column(verticalArrangement = Arrangement.spacedBy(14.dp)) {
            Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween) {
                Text("Follow-Up Channels", color = theme.primaryText, style = MaterialTheme.typography.titleMedium)
                HavnButton("View report", onClick = {}, style = HavnButtonStyle.Secondary)
            }
            rows.ifEmpty { listOf(SourceRow("No follow-ups yet", 0.0, theme.border)) }.forEach { row ->
                Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(10.dp), modifier = Modifier.fillMaxWidth()) {
                    Surface(Modifier.size(24.dp), shape = CircleShape, color = row.color.copy(alpha = 0.18f)) {}
                    Text(row.label, color = theme.primaryText, style = MaterialTheme.typography.labelLarge, modifier = Modifier.weight(1f), maxLines = 1, overflow = TextOverflow.Ellipsis)
                    Surface(modifier = Modifier.width(110.dp).height(7.dp), shape = CircleShape, color = theme.border.copy(alpha = 0.45f)) {
                        Box(Modifier.fillMaxWidth((row.percent / 100.0).toFloat().coerceIn(0.05f, 1f)).background(row.color))
                    }
                    Text("${row.percent.oneDecimal()}%", color = theme.primaryText, style = MaterialTheme.typography.labelLarge)
                }
            }
        }
    }
}

private data class FunnelStage(val value: Int, val label: String, val color: Color)
private data class ChartSeries(val label: String, val color: Color, val values: List<Int>)
private data class SourceRow(val label: String, val percent: Double, val color: Color)
private data class AgentPerformanceRow(
    val name: String,
    val openHouses: Int,
    val hotLeads: Int,
    val followRate: Int,
    val conversion: Double
)

private enum class AnalyticsDateFilter(val label: String, val dayWindow: Int?) {
    All("All activity", null),
    Latest30("Latest 30 days", 30),
    Latest7("Latest 7 days", 7);

    fun next(): AnalyticsDateFilter = when (this) {
        All -> Latest30
        Latest30 -> Latest7
        Latest7 -> All
    }

    fun labelFor(range: String): String = when (this) {
        All -> range
        else -> label
    }
}

private data class AnalyticsAgentOption(
    val agentId: String?,
    val label: String
)

private data class AnalyticsFilteredData(
    val properties: List<Property>,
    val openHouses: List<OpenHouse>,
    val leads: List<Lead>,
    val followUps: List<FollowUp>,
    val activityDateRange: String?
)

private data class AnalyticsMetrics(
    val dateRange: String,
    val activeProperties: Int,
    val openHouseCount: Int,
    val visitors: Int,
    val totalLeads: Int,
    val interestedBuyers: Int,
    val hotLeads: Int,
    val warmLeads: Int,
    val coldLeads: Int,
    val tourSignals: Int,
    val offerSignals: Int,
    val conversion: Double,
    val averageIntentLabel: String,
    val completedFollowUps: Int,
    val followUpRate: Int,
    val topProperties: List<Property>,
    val trendSeries: List<ChartSeries>,
    val insightText: String,
    val agentRows: List<AgentPerformanceRow>,
    val channelRows: List<SourceRow>
)

private fun buildAgentOptions(members: List<AgencyMember>, currentAgentId: String?): List<AnalyticsAgentOption> {
    val memberOptions = members
        .mapNotNull { member ->
            member.agentId?.let { AnalyticsAgentOption(it, member.fullName.ifBlank { member.email }) }
        }
        .distinctBy { it.agentId }
    return buildList {
        add(AnalyticsAgentOption(null, "All agents"))
        addAll(memberOptions)
        if (currentAgentId != null && memberOptions.none { it.agentId == currentAgentId }) {
            add(AnalyticsAgentOption(currentAgentId, "Me"))
        }
    }
}

private fun List<AnalyticsAgentOption>.nextAgentId(current: String?): String? {
    if (isEmpty()) return null
    val index = indexOfFirst { it.agentId == current }.takeIf { it >= 0 } ?: 0
    return get((index + 1) % size).agentId
}

private fun filterAnalyticsData(
    dateFilter: AnalyticsDateFilter,
    agentId: String?,
    properties: List<Property>,
    openHouses: List<OpenHouse>,
    leads: List<Lead>,
    followUps: List<FollowUp>
): AnalyticsFilteredData {
    val dateAnchor = listOf(
        openHouses.mapNotNull { it.startsAt.isoDayNumberOrNull() },
        leads.mapNotNull { it.checkedInAt.isoDayNumberOrNull() },
        followUps.mapNotNull { (it.completedAt ?: it.dueAt).isoDayNumberOrNull() }
    ).flatten().maxOrNull()
    val minDay = dateFilter.dayWindow?.let { window -> dateAnchor?.minus(window - 1) }
    fun inDate(value: String?): Boolean {
        val day = value.isoDayNumberOrNull() ?: return minDay == null
        return minDay == null || day >= minDay
    }

    val agentPropertyIds = if (agentId == null) {
        properties.map { it.id }.toSet()
    } else {
        properties.filter {
            it.agentId == agentId || it.ownerAgentId == agentId || it.assignedAgentId == agentId
        }.map { it.id }.toSet()
    }
    val agentOpenHouses = openHouses.filter { opening ->
        (agentId == null || opening.hostAgentId == agentId || opening.propertyId in agentPropertyIds) &&
            inDate(opening.startsAt)
    }
    val visibleOpenHouseIds = agentOpenHouses.map { it.id }.toSet()
    val visiblePropertyIdsFromOpenings = agentOpenHouses.map { it.propertyId }.toSet()
    val filteredLeads = leads.filter { lead ->
        (lead.openHouseId in visibleOpenHouseIds || lead.propertyId in agentPropertyIds) &&
            inDate(lead.checkedInAt)
    }
    val visibleVisitorIds = filteredLeads.mapNotNull { it.visitorId }.toSet()
    val filteredFollowUps = followUps.filter { followUp ->
        followUp.visitorId in visibleVisitorIds &&
            inDate(followUp.completedAt ?: followUp.dueAt)
    }
    val visiblePropertyIds = (agentPropertyIds.takeIf { agentId != null } ?: properties.map { it.id }.toSet())
        .intersect(
            (visiblePropertyIdsFromOpenings + filteredLeads.map { it.propertyId }).takeIf { it.isNotEmpty() }
                ?: (agentPropertyIds.takeIf { agentId != null } ?: properties.map { it.id }.toSet())
        )
    val filteredProperties = properties.filter { property ->
        property.id in visiblePropertyIds &&
            (minDay == null || property.id in visiblePropertyIdsFromOpenings || property.id in filteredLeads.map { it.propertyId }.toSet() || inDate(property.createdAt))
    }
    val activityDates = (agentOpenHouses.map { it.startsAt } + filteredLeads.map { it.checkedInAt })
        .filter { inDate(it) }
    return AnalyticsFilteredData(
        properties = filteredProperties,
        openHouses = agentOpenHouses,
        leads = filteredLeads,
        followUps = filteredFollowUps,
        activityDateRange = activityDates.dateRangeLabelFromStrings()
    )
}

private fun buildAnalyticsMetrics(
    properties: List<Property>,
    openHouses: List<OpenHouse>,
    leads: List<Lead>,
    followUps: List<FollowUp>,
    members: List<AgencyMember>
): AnalyticsMetrics {
    val visitorCountFromOpenHouses = openHouses.sumOf { it.stats.visitorCount }
    val visitors = visitorCountFromOpenHouses.coerceAtLeast(leads.size)
    val hotLeads = leads.count { it.temperature == LeadTemperature.Hot }
        .coerceAtLeast(openHouses.sumOf { it.stats.hotCount })
    val warmLeads = leads.count { it.temperature == LeadTemperature.Warm }
        .coerceAtLeast(openHouses.sumOf { it.stats.warmCount })
    val coldLeads = leads.count { it.temperature == LeadTemperature.Cold }
        .coerceAtLeast(openHouses.sumOf { it.stats.coldCount })
    val interested = (hotLeads + warmLeads).coerceAtMost(visitors)
    val tourSignals = countTextSignals(leads, followUps, listOf("tour", "showing", "second showing", "private"))
    val offerSignals = countTextSignals(leads, followUps, listOf("offer", "bid", "contract"))
    val completedFollowUps = followUps.count { it.status == FollowUpStatus.Completed }
    val followUpRate = if (leads.isEmpty()) 0 else (completedFollowUps.toDouble() / leads.size.toDouble() * 100).roundToInt().coerceIn(0, 100)
    val conversion = if (visitors == 0) 0.0 else hotLeads.toDouble() / visitors.toDouble() * 100.0
    val averageIntent = leads.mapNotNull { it.intentScore }.averageOrNull()
    val topProperties = properties.sortedByDescending {
        it.stats.totalVisitors + it.stats.hotLeads * 5 + it.stats.followUpsSent * 2
    }.take(5)
    val openHousesById = openHouses.associateBy { it.id }
    val memberNameByAgent = members.mapNotNull { member ->
        member.agentId?.let { it to member.fullName }
    }.toMap()
    val visitorToOpenHouse = leads.mapNotNull { lead ->
        lead.visitorId?.let { it to lead.openHouseId }
    }.toMap()
    val agentRows = openHouses.groupBy { it.hostAgentId }
        .map { (agentId, hosted) ->
            val hostedIds = hosted.map { it.id }.toSet()
            val hostedLeads = leads.filter { it.openHouseId in hostedIds }
            val hostedVisitorIds = hostedLeads.mapNotNull { it.visitorId }.toSet()
            val hostedFollowUps = followUps.filter { it.visitorId in hostedVisitorIds }
            val hostedVisitors = hosted.sumOf { it.stats.visitorCount }.coerceAtLeast(hostedLeads.size)
            val hostedHot = hostedLeads.count { it.temperature == LeadTemperature.Hot }.coerceAtLeast(hosted.sumOf { it.stats.hotCount })
            AgentPerformanceRow(
                name = memberNameByAgent[agentId] ?: "Agent",
                openHouses = hosted.size,
                hotLeads = hostedHot,
                followRate = if (hostedLeads.isEmpty()) 0 else (hostedFollowUps.count { it.status == FollowUpStatus.Completed }.toDouble() / hostedLeads.size.toDouble() * 100).roundToInt().coerceIn(0, 100),
                conversion = if (hostedVisitors == 0) 0.0 else hostedHot.toDouble() / hostedVisitors.toDouble() * 100.0
            )
        }
        .sortedByDescending { it.hotLeads + it.openHouses }
    val trend = buildTrendSeries(openHouses, leads, followUps, visitorToOpenHouse)
    val channelRows = buildChannelRows(followUps)
    val insight = buildInsight(visitors, hotLeads, warmLeads, completedFollowUps, followUpRate, topProperties.firstOrNull())

    return AnalyticsMetrics(
        dateRange = openHouses.dateRangeLabel(),
        activeProperties = properties.count { property ->
            property.id in openHouses.map { it.propertyId }.toSet() ||
                property.id in leads.map { it.propertyId }.toSet() ||
                property.stats.totalVisitors > 0 ||
                property.stats.hotLeads > 0
        }.coerceAtLeast(properties.size.takeIf { it > 0 && openHouses.isEmpty() && leads.isEmpty() } ?: 0),
        openHouseCount = openHouses.size,
        visitors = visitors,
        totalLeads = leads.size,
        interestedBuyers = interested,
        hotLeads = hotLeads,
        warmLeads = warmLeads,
        coldLeads = coldLeads,
        tourSignals = tourSignals,
        offerSignals = offerSignals,
        conversion = conversion,
        averageIntentLabel = averageIntent?.let { "${it.oneDecimal()}/5" } ?: "No data",
        completedFollowUps = completedFollowUps,
        followUpRate = followUpRate,
        topProperties = topProperties,
        trendSeries = trend,
        insightText = insight,
        agentRows = agentRows,
        channelRows = channelRows
    )
}

private fun buildTrendSeries(
    openHouses: List<OpenHouse>,
    leads: List<Lead>,
    followUps: List<FollowUp>,
    visitorToOpenHouse: Map<String, String>
): List<ChartSeries> {
    val ordered = openHouses.sortedBy { it.startsAt }.takeLast(30)
    val labels = ordered.map { it.id }
    val hotByOpening = leads.filter { it.temperature == LeadTemperature.Hot }.groupingBy { it.openHouseId }.eachCount()
    val tourOpeningIds = followUps
        .filter { it.textContainsAny(listOf("tour", "showing", "private")) }
        .mapNotNull { visitorToOpenHouse[it.visitorId] }
        .groupingBy { it }
        .eachCount()
    return listOf(
        ChartSeries("Visitors", Color(0xFF3B82F6), ordered.map { it.stats.visitorCount }),
        ChartSeries("Hot Leads", Color(0xFF22C55E), labels.map { hotByOpening[it] ?: 0 }),
        ChartSeries("Tour Signals", Color(0xFFF59E0B), labels.map { tourOpeningIds[it] ?: 0 }),
        ChartSeries("Follow-Ups", Color(0xFF8B5CF6), ordered.map { opening ->
            val visitorIds = leads.filter { it.openHouseId == opening.id }.mapNotNull { it.visitorId }.toSet()
            followUps.count { it.visitorId in visitorIds && it.status == FollowUpStatus.Completed }
        })
    )
}

private fun buildChannelRows(followUps: List<FollowUp>): List<SourceRow> {
    val total = followUps.size.coerceAtLeast(1)
    return FollowUpChannel.entries.map { channel ->
        val count = followUps.count { it.channel == channel }
        SourceRow(channel.label, count.toDouble() / total.toDouble() * 100.0, channel.color())
    }.filter { it.percent > 0.0 }
}

private fun buildInsight(
    visitors: Int,
    hotLeads: Int,
    warmLeads: Int,
    completedFollowUps: Int,
    followUpRate: Int,
    topProperty: Property?
): String {
    val propertyLine = topProperty?.let { "${it.title} is currently the strongest property by visitor and hot-lead activity. " } ?: ""
    return when {
        visitors == 0 -> "No visitor registrations are available yet. Analytics will become more useful after the next open house sign-ins."
        hotLeads > 0 -> "${propertyLine}$hotLeads hot leads and $warmLeads warm leads are visible from registered buyer activity. Follow-up completion is $followUpRate%."
        completedFollowUps > 0 -> "${propertyLine}Buyer activity is early, but $completedFollowUps follow-ups have already been completed. Watch hot-lead conversion after the next event."
        else -> "${propertyLine}$visitors visitors are registered. Start follow-ups to create stronger conversion signals."
    }
}

private fun countTextSignals(leads: List<Lead>, followUps: List<FollowUp>, terms: List<String>): Int {
    val leadSignals = leads.count { lead ->
        lead.notes?.containsAny(terms) == true
    }
    val followUpSignals = followUps.count { it.textContainsAny(terms) }
    return leadSignals + followUpSignals
}

private fun FollowUp.textContainsAny(terms: List<String>): Boolean =
    subject.containsAny(terms) || body?.containsAny(terms) == true

private fun String.containsAny(terms: List<String>): Boolean =
    terms.any { contains(it, ignoreCase = true) }

private fun List<Int>.averageOrNull(): Double? = if (isEmpty()) null else average()

private fun FollowUpChannel.color(): Color = when (this) {
    FollowUpChannel.Call -> Color(0xFFF59E0B)
    FollowUpChannel.Sms -> Color(0xFF22C55E)
    FollowUpChannel.Email -> Color(0xFF3B82F6)
    FollowUpChannel.Broker -> Color(0xFF8B5CF6)
    FollowUpChannel.Note -> Color(0xFF94A3B8)
}

private fun List<OpenHouse>.dateRangeLabel(): String {
    val sorted = sortedBy { it.startsAt }
    val start = sorted.firstOrNull()?.startsAt?.dateLabel() ?: return "No open houses yet"
    val end = sorted.lastOrNull()?.startsAt?.dateLabel() ?: start
    return if (start == end) start else "$start - $end"
}

private fun List<String>.dateRangeLabelFromStrings(): String? {
    val sorted = filter { it.isNotBlank() }.sorted()
    val start = sorted.firstOrNull()?.dateLabel() ?: return null
    val end = sorted.lastOrNull()?.dateLabel() ?: start
    return if (start == end) start else "$start - $end"
}

private fun String?.isoDayNumberOrNull(): Int? {
    if (this.isNullOrBlank()) return null
    val parts = substringBefore("T").split("-")
    if (parts.size != 3) return null
    val year = parts[0].toIntOrNull() ?: return null
    val month = parts[1].toIntOrNull() ?: return null
    val day = parts[2].toIntOrNull() ?: return null
    if (month !in 1..12 || day !in 1..31) return null
    val monthOffset = intArrayOf(0, 31, 59, 90, 120, 151, 181, 212, 243, 273, 304, 334)
    val leapDays = (year - 1) / 4 - (year - 1) / 100 + (year - 1) / 400
    val leap = year % 4 == 0 && (year % 100 != 0 || year % 400 == 0)
    return year * 365 + leapDays + monthOffset[month - 1] + day + if (leap && month > 2) 1 else 0
}

private fun String.dateLabel(): String {
    val date = substringBefore("T")
    val parts = date.split("-")
    if (parts.size != 3) return date
    val month = when (parts[1]) {
        "01" -> "Jan"
        "02" -> "Feb"
        "03" -> "Mar"
        "04" -> "Apr"
        "05" -> "May"
        "06" -> "Jun"
        "07" -> "Jul"
        "08" -> "Aug"
        "09" -> "Sep"
        "10" -> "Oct"
        "11" -> "Nov"
        else -> "Dec"
    }
    return "$month ${parts[2].trimStart('0').ifBlank { "1" }}, ${parts[0]}"
}

private fun Double.oneDecimal(): String {
    val rounded = kotlin.math.round(this * 10.0) / 10.0
    return if (rounded % 1.0 == 0.0) rounded.toInt().toString() else rounded.toString()
}
